import { zodResolver } from "@hookform/resolvers/zod";
import { useBlocker, useNavigate, useRouter } from "@tanstack/react-router";
import React from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import * as schemas from "@blis/shared/schemas/process";
import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Heading } from "@/components/ui/heading";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { ProcessTemplateDetail } from "../../types";
import { EditorField } from "./ProcessDetailForm";

type CreateVersionFormValues = z.infer<typeof schemas.createVersion>;
type UpdateVersionFormValues = z.infer<typeof schemas.updateVersion>;

interface ProcessVersionEditorProps {
  template: ProcessTemplateDetail;
  versionId: string; // "new" for create mode, or numeric ID for update mode
}

export function ProcessVersionEditor({
  template,
  versionId,
}: ProcessVersionEditorProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const [showUpdateWarning, setShowUpdateWarning] = React.useState(false);

  const isCreateMode = versionId === "new";
  const versionIdNumber = isCreateMode
    ? undefined
    : Number.parseInt(versionId, 10);

  // Check for preserved definition from navigation state
  const preservedDefinition = router.state.location.state?.preserveDefinition;

  // Query for existing version data (only in update mode)
  const existingVersion = trpc.admin.process.getVersion.useQuery(
    { id: versionIdNumber ?? 0 },
    { enabled: !isCreateMode && !!versionIdNumber },
  );

  const createVersion = trpc.admin.process.createVersion.useMutation(
    mutationResponse({
      errorTitle: "Failed to create process template version",
      successMessage: "Process template version created",
      onSuccess: () => {
        navigate({
          to: "/admin/processes/$id",
          params: { id: template.id },
        });
      },
    }),
  );

  const updateVersion = trpc.admin.process.updateVersion.useMutation(
    mutationResponse({
      errorTitle: "Failed to update process template version",
      successMessage: "Process template version updated",
    }),
  );

  // Get base definition for create mode (latest version's definition)
  const getBaseDefinition = () => {
    // Use preserved definition if available (when switching from update to create mode)
    if (preservedDefinition) {
      return preservedDefinition;
    }

    if (isCreateMode) {
      const latestVersion = template.versions[0];
      if (latestVersion?.definition) {
        return typeof latestVersion.definition === "object"
          ? latestVersion.definition
          : JSON.parse(latestVersion.definition as string);
      }
      return {};
    }

    if (existingVersion.data?.definition) {
      return typeof existingVersion.data.definition === "object"
        ? existingVersion.data.definition
        : JSON.parse(existingVersion.data.definition as string);
    }

    return {};
  };

  // Get next version number for create mode
  const getNextVersionNumber = () => {
    if (template.versions.length === 0) return 1;
    const latestVersion = Math.max(...template.versions.map((v) => v.version));
    return latestVersion + 1;
  };

  const form = useForm<CreateVersionFormValues | UpdateVersionFormValues>({
    defaultValues: isCreateMode
      ? {
          templateId: template.id,
          definition: getBaseDefinition(),
        }
      : {
          id: versionIdNumber ?? 0,
          definition: getBaseDefinition(),
        },
    resolver: zodResolver(
      isCreateMode ? schemas.createVersion : schemas.updateVersion,
    ),
  });

  // Update form when existing version data loads
  React.useEffect(() => {
    if (!isCreateMode && existingVersion.data?.definition) {
      const definition =
        typeof existingVersion.data.definition === "object"
          ? existingVersion.data.definition
          : JSON.parse(existingVersion.data.definition as string);

      form.setValue("definition", definition);
    }
  }, [existingVersion.data?.definition, isCreateMode, form]);

  // Update form when preserved definition is available (when switching to create mode)
  React.useEffect(() => {
    if (isCreateMode && preservedDefinition) {
      form.setValue("definition", preservedDefinition);
      // Clear the preserved definition from state to avoid persisting it
      navigate({
        to: "/admin/processes/$id/versions/$versionId",
        params: {
          id: template.id,
          versionId: "new",
        },
        state: {
          ...router.state.location.state,
          preserveDefinition: undefined,
        },
        replace: true,
      });
    }
  }, [preservedDefinition, isCreateMode, form, router, navigate, template.id]);

  const onSubmit: SubmitHandler<
    CreateVersionFormValues | UpdateVersionFormValues
  > = (values) => {
    if (isCreateMode) {
      createVersion.mutate(values as CreateVersionFormValues);
    } else {
      updateVersion.mutate(values as UpdateVersionFormValues);
    }
  };

  const hasUnsavedChanges = form.formState.isDirty;

  useBlocker({
    shouldBlockFn: () => {
      if (hasUnsavedChanges) {
        const shouldLeave = confirm("Are you sure you want to leave?");
        return !shouldLeave;
      }
      return false;
    },
  });

  const handleSave = () => {
    if (!isCreateMode) {
      // Show warning dialog for updates
      setShowUpdateWarning(true);
    } else {
      // Create mode - submit directly
      form.handleSubmit(onSubmit)();
    }
  };

  const handleConfirmUpdate = () => {
    setShowUpdateWarning(false);
    form.handleSubmit(onSubmit)();
  };

  const handleCreateNewVersion = () => {
    setShowUpdateWarning(false);
    // Navigate to create mode with current form data
    const formData = form.getValues();
    navigate({
      to: "/admin/processes/$id/versions/$versionId",
      params: {
        id: template.id,
        versionId: "new",
      },
      state: {
        // Pass current definition to preserve changes
        preserveDefinition: formData.definition,
      },
    });
  };

  const currentVersion = isCreateMode
    ? { version: getNextVersionNumber(), createdAt: new Date().toISOString() }
    : existingVersion.data;

  const isLoading = !isCreateMode && existingVersion.isLoading;
  const mutation = isCreateMode ? createVersion : updateVersion;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col h-full">
        {/* Update Warning Dialog */}
        <Modal isOpen={showUpdateWarning} onOpenChange={setShowUpdateWarning}>
          <Dialog>
            <DialogHeader>
              <DialogTitle>Update Existing Version?</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Text className="mb-4">
                You are about to update Version {currentVersion?.version} of
                this process template.
              </Text>
              <Text className="text-sm text-muted-foreground">
                Updating an existing version will modify it permanently.
                Consider creating a new version instead to preserve the current
                version and maintain version history.
              </Text>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="outline"
                onPress={() => setShowUpdateWarning(false)}
              >
                Cancel
              </Button>
              <Button variant="outline" onPress={handleCreateNewVersion}>
                Create New Version
              </Button>
              <Button
                onPress={handleConfirmUpdate}
                isPending={mutation.isPending}
              >
                Update Version
              </Button>
            </DialogFooter>
          </Dialog>
        </Modal>

        <div className="flex-shrink-0 space-y-4">
          {/* Template Context */}
          <div className="bg-accent-muted rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Text className="block text-sm font-medium text-muted-foreground">
                  Template
                </Text>
                <Text className="block mt-2">{template.name}</Text>
              </div>
              <div>
                <Text className="block text-sm font-medium text-muted-foreground">
                  Version
                </Text>
                <Text className="block mt-2">
                  {isCreateMode
                    ? `Version ${getNextVersionNumber()} (New)`
                    : `Version ${currentVersion?.version}`}
                </Text>
              </div>
              <div>
                <Text className="block text-sm font-medium text-muted-foreground">
                  {isCreateMode ? "Will be created" : "Created"}
                </Text>
                <Text className="block mt-2">
                  {isCreateMode
                    ? "On save"
                    : currentVersion?.createdAt
                      ? new Date(currentVersion.createdAt).toLocaleDateString()
                      : ""}
                </Text>
              </div>
            </div>
          </div>

          {/* Definition Header */}
          <div className="flex items-center justify-between">
            <Heading level={3}>Definition</Heading>

            {hasUnsavedChanges && (
              <Badge variant="outline" className="text-amber-700">
                • Unsaved changes
              </Badge>
            )}
          </div>
        </div>

        <EditorField />

        <div className="flex-shrink-0 flex items-center justify-between mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            <span>
              {isCreateMode
                ? `Creating Version ${getNextVersionNumber()}`
                : `Editing Version ${currentVersion?.version}`}
            </span>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onPress={() => {
                navigate({
                  to: "/admin/processes/$id",
                  params: { id: template.id },
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onPress={handleSave}
              isDisabled={mutation.isPending || !form.formState.isValid}
              isPending={mutation.isPending}
            >
              {isCreateMode ? "Create Version" : "Update Version"}
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
